import { useOfflineSync } from "@/lib/offline-sync";
import { cn } from "@/lib/utils";
import { Loader2, WifiOff, RefreshCw } from "lucide-react";

export function SyncBanner({ className }: { className?: string }) {
    const { pendingCount, sync, isOnline, syncing } = useOfflineSync();

    // Online with an empty queue renders nothing — even mid-drain. A
    // "Syncing 0 pending items…" banner is pure layout noise (it strobed
    // the field tabs by shoving the page down and back on every drain).
    if (isOnline && pendingCount === 0) return null;

    return (
        <div
            role="status"
            aria-live="polite"
            className={cn(
                "flex items-center justify-between gap-3 rounded-lg px-4 py-2.5 text-sm font-medium",
                !isOnline
                    ? "bg-warning/10 text-warning border border-warning/20"
                    : syncing
                      ? "bg-primary/10 text-primary border border-primary/20"
                      : "bg-sky-50 text-sky-700 border border-sky-200",
                className,
            )}
        >
            <div className="flex items-center gap-2">
                {syncing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                ) : !isOnline ? (
                    <WifiOff className="h-4 w-4" />
                ) : (
                    <RefreshCw className="h-4 w-4" />
                )}
                <span>
                    {syncing
                        ? `Syncing ${pendingCount} pending ${pendingCount === 1 ? "item" : "items"}…`
                        : !isOnline
                          ? `Offline — ${pendingCount} ${pendingCount === 1 ? "item" : "items"} queued`
                          : `${pendingCount} ${pendingCount === 1 ? "item" : "items"} pending sync`}
                </span>
            </div>
            {isOnline && !syncing && pendingCount > 0 && (
                <button
                    type="button"
                    onClick={() => void sync()}
                    className="shrink-0 rounded-md bg-sky-700 px-3 py-1 text-xs font-semibold text-white hover:bg-sky-800 transition-colors"
                >
                    Sync Now
                </button>
            )}
        </div>
    );
}
